import { inject, Injectable } from '@angular/core';
import { Auth, getIdToken, signOut } from '@angular/fire/auth';

import { getErrorCode } from './error-reporting.service';
import { SessionStateService } from './session-state.service';
import { clearSessionScopedStorage } from '../util/session-storage';

export type SessionEndedReason = 'account-disabled' | 'session-expired';

/**
 * Maps an auth error from a forced token refresh onto the reason the session
 * ended, or null when the failure says nothing about the session's validity.
 *
 * Only definitive codes end a session. A refresh that failed because the
 * network dropped must never sign anyone out — the token is probably fine and
 * the user would lose their work for a blip of connectivity.
 */
export function sessionEndedReason(code: string | undefined): SessionEndedReason | null {
  switch (code) {
    case 'auth/user-disabled':
      return 'account-disabled';
    case 'auth/user-token-expired':
    case 'auth/invalid-user-token':
    case 'auth/user-not-found':
      return 'session-expired';
    default:
      return null;
  }
}

/**
 * Decides whether a Firestore permission denial means "you cannot see this"
 * or "your session is over", and ends the session when it is the latter.
 *
 * The two are indistinguishable from the error alone — both arrive as
 * `permission-denied` — so the app used to render its "forbidden" panel for
 * both. A user whose account had been closed was told they lacked access to
 * the office they were looking at, which reads as a permissions bug rather
 * than a closed account (TRACIT-WEB-2).
 *
 * A forced token refresh settles it: it is the one call that asks the auth
 * backend, rather than the local cache, whether this session is still real.
 */
@Injectable({ providedIn: 'root' })
export class SessionValidityService {
  private readonly auth = inject(Auth, { optional: true });
  private readonly sessionState = inject(SessionStateService);

  private probeInFlight?: Promise<void>;
  private hasEnded = false;

  /**
   * Call from a root listener's error handler. Cheap and safe to call on every
   * denial: non-denials are ignored, and concurrent denials share one probe
   * rather than each forcing their own token refresh.
   */
  checkAfterPermissionDenied(error: unknown): void {
    if (this.hasEnded || getErrorCode(error) !== 'permission-denied') {
      return;
    }

    this.probeInFlight ??= this.probe().finally(() => this.probeInFlight = undefined);
  }

  private async probe(): Promise<void> {
    const user = this.auth?.currentUser;

    if (!user) {
      // Already signed out, or auth is absent under SSR. Either way the
      // sign-out path has run or does not apply.
      return;
    }

    try {
      await getIdToken(user, true);
    } catch (error) {
      const reason = sessionEndedReason(getErrorCode(error));

      if (reason) {
        await this.endSession(reason);
      }
    }
  }

  private async endSession(reason: SessionEndedReason): Promise<void> {
    if (this.hasEnded) {
      return;
    }

    this.hasEnded = true;

    // Stops the root services re-arming their listeners in the window between
    // signing out and the reload below.
    this.sessionState.beginSignOut();

    // The reload discards every service's in-memory state, so the only cleanup
    // that has to happen here is the part that would otherwise outlive it and
    // leak into the next person's session on a shared machine.
    //
    // This deliberately does not call SessionCleanupService: it depends on
    // nearly every root data service, several of which report denials through
    // this service, and importing it here closed a module cycle that broke the
    // SSR prerender with a TDZ error on AuthenticatedDataService.
    clearSessionScopedStorage();

    try {
      // Signing out is what makes this terminal. The credential persists in
      // IndexedDB, so reloading without clearing it would restore the same dead
      // session, fail the same reads, and land back here — a redirect loop.
      if (this.auth) {
        await signOut(this.auth);
      }
    } catch (error) {
      console.error('[session-validity] Failed to sign out cleanly.', error);
    } finally {
      if (typeof window !== 'undefined') {
        // A full document load rather than a route change: it guarantees
        // nothing from the dead session survives into the signed-out app.
        window.location.replace(`/signin?from=${reason}`);
      }
    }
  }
}
